import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import ProviderTileComponentCarousel from '../../ProviderTile/ProviderTileComponentCarousel';
import ProviderTileComponentCrossSellCarousel from '../../ProviderTile/ProviderTileComponentCrossSellCarousel';
import { useMobileContext } from '../../../contexts/MobileContext';
import { useNavigate } from 'react-router-dom';
import {
  getAllCrossSellBestDealQuotes,
  normalizeStoredQuotesForCarousel,
  getProductConfigByCrossSellKey,
  isProductChosen,
} from '../../../utils/carouselQuoteHelpers';

// ===== Marquee + Interaction Tunables =====
const PIXELS_PER_SECOND_DESKTOP = 90;
const PIXELS_PER_SECOND_MOBILE = 70;
const RESUME_IDLE_MS = 2500;
const MIN_THUMB_PX = 24;

// Snap/edge thresholds
const EPS = 0.75; // float safety
const SNAP_PX = 24; // only snap if we're farther than this from a card start

const parseStoredQuotes = (storageKey) => {
  const raw = localStorage.getItem(storageKey);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (Array.isArray(parsed?.Quotes)) return parsed.Quotes;
  } catch {
    // ignore
  }
  return [];
};

const QuoteCarouselComponent = ({
  crossSellKey,
  fallbackKey,
  variant = 'quote',
} = {}) => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [hasOverflow, setHasOverflow] = useState(false);
  const isMobileCtx = useMobileContext();
  const isMobile = !!isMobileCtx?.isMobile;

  // DOM refs
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);

  // marquee + timing
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const pausedRef = useRef(false);
  const resumeTimerRef = useRef(null);
  const speedRef = useRef(
    isMobile ? PIXELS_PER_SECOND_MOBILE : PIXELS_PER_SECOND_DESKTOP
  );
  const dirRef = useRef(1); // +1 forward, -1 backward

  // custom scrollbar drag
  const draggingThumbRef = useRef(false);
  const dragStartXRef = useRef(0);
  const initialScrollLeftRef = useRef(0);

  // rAF throttle for thumb updates
  const thumbAF = useRef(null);

  useEffect(() => {
    if (crossSellKey && fallbackKey) {
      const config = getProductConfigByCrossSellKey(crossSellKey);

      let loaded = [];
      let activeStorageKey = crossSellKey;

      if (!isProductChosen(fallbackKey)) {
        // Scenario 2: session quotes take priority — show exactly what the user just saw
        const sessionQuotes = normalizeStoredQuotesForCarousel(
          parseStoredQuotes(fallbackKey),
          fallbackKey
        );
        if (sessionQuotes.length > 0) {
          loaded = sessionQuotes;
          activeStorageKey = fallbackKey;
        } else {
          // Scenario 3: no session quotes — fall back to cross-sell
          loaded = normalizeStoredQuotesForCarousel(
            parseStoredQuotes(crossSellKey),
            crossSellKey
          );
          activeStorageKey = crossSellKey;
        }
      }
      // Scenario 1: isProductChosen → loaded stays [], carousel hidden

      setQuotes(
        loaded.map((q) => ({
          ...q,
          navigateTo: config?.navigateTo,
          crossSellStorageKey: activeStorageKey,
        }))
      );
    } else {
      setQuotes(getAllCrossSellBestDealQuotes());
    }
  }, [crossSellKey, fallbackKey]);

  /** ---------------------------
   * Helpers: dimensions & thumb
   * --------------------------- */
  const getMaxScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return 0;
    return Math.max(0, el.scrollWidth - el.clientWidth);
  }, []);

  const checkOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setHasOverflow(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    if (quotes.length === 0) return;
    const raf = requestAnimationFrame(checkOverflow);
    return () => cancelAnimationFrame(raf);
  }, [quotes, checkOverflow]);

  const isAtLeftEdge = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return false;
    return el.scrollLeft <= EPS;
  }, []);

  const isAtRightEdge = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return false;
    const max = getMaxScroll();
    return el.scrollLeft >= max - EPS;
  }, [getMaxScroll]);

  // const isAtEdge = useCallback(
  //   () => isAtLeftEdge() || isAtRightEdge(),
  //   [isAtLeftEdge, isAtRightEdge]
  // );

  const updateThumb = useCallback(() => {
    const el = scrollRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!el || !track || !thumb) return;

    const maxScroll = getMaxScroll();
    const trackW = track.clientWidth;

    if (maxScroll <= 0) {
      thumb.style.width = `${trackW}px`;
      thumb.style.left = `0px`;
      return;
    }

    const ratio = el.clientWidth / (el.clientWidth + maxScroll); // visible / total
    const width = Math.max(MIN_THUMB_PX, ratio * trackW);
    const left = (el.scrollLeft / maxScroll) * (trackW - width);

    thumb.style.width = `${width}px`;
    thumb.style.left = `${Math.max(0, Math.min(trackW - width, left))}px`;
  }, [getMaxScroll]);

  const scheduleThumbUpdate = useCallback(() => {
    if (thumbAF.current) return;
    thumbAF.current = requestAnimationFrame(() => {
      updateThumb();
      thumbAF.current = null;
    });
  }, [updateThumb]);

  /** ---------------------------
   * Edge helpers for autoplay
   * --------------------------- */
  const startMovingAwayFromEdge = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = getMaxScroll();
    if (el.scrollLeft <= EPS) dirRef.current = 1;
    else if (el.scrollLeft >= max - EPS) dirRef.current = -1;
  }, [getMaxScroll]);

  const nudgeInsideFromEdge = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = getMaxScroll();
    // Move a hair inside so autoplay can start next frame without a visible jerk.
    if (el.scrollLeft <= EPS) {
      el.scrollLeft = Math.min(1, max);
    } else if (el.scrollLeft >= max - EPS) {
      el.scrollLeft = Math.max(0, max - 1);
    }
  }, [getMaxScroll]);

  /** ---------------------------
   * Ping-pong marquee core
   * --------------------------- */
  const tick = useCallback(
    (ts) => {
      const el = scrollRef.current;
      if (!el) return;

      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000; // seconds
      lastTsRef.current = ts;

      if (!pausedRef.current) {
        const maxScroll = getMaxScroll();
        if (maxScroll > 0) {
          const delta = speedRef.current * dt * dirRef.current;
          let next = el.scrollLeft + delta;

          // Boundary detection with small epsilon for float safety
          if (next <= EPS) {
            next = 0;
            dirRef.current = 1; // bounce forward
          } else if (next >= maxScroll - EPS) {
            next = maxScroll;
            dirRef.current = -1; // bounce backward
          }

          el.scrollLeft = next;
          scheduleThumbUpdate();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    },
    [getMaxScroll, scheduleThumbUpdate]
  );

  const startRAF = useCallback(() => {
    stopRAF();
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const stopRAF = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const pause = useCallback(() => {
    pausedRef.current = true;
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  }, []);

  const resumeSoon = useCallback(() => {
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
    resumeTimerRef.current = setTimeout(() => {
      // Resume even if we're at an edge: set direction, nudge inside, then unpause.
      startMovingAwayFromEdge();
      nudgeInsideFromEdge();
      pausedRef.current = false;
    }, RESUME_IDLE_MS);
  }, [startMovingAwayFromEdge, nudgeInsideFromEdge]);

  /** ---------------------------
   * Lifecycle & sizing
   * --------------------------- */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Start from the beginning and go forward initially
    el.scrollLeft = 0;
    dirRef.current = 1;
    updateThumb();
    startRAF();

    const onResize = () => {
      updateThumb();
      checkOverflow();
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      stopRAF();
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      if (thumbAF.current) cancelAnimationFrame(thumbAF.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes.length, hasOverflow]);

  useEffect(() => {
    speedRef.current = isMobile
      ? PIXELS_PER_SECOND_MOBILE
      : PIXELS_PER_SECOND_DESKTOP;
  }, [isMobile]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => scheduleThumbUpdate();
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [scheduleThumbUpdate]);

  /** ---------------------------
   * Manual interaction (scroll area)
   * --------------------------- */
  const onPointerDownScrollArea = () => {
    pause();
  };
  const onPointerUpScrollArea = () => {
    resumeSoon();
  };
  const onTouchStartArea = () => {
    pause();
  };
  const onTouchEndArea = () => {
    resumeSoon();
  };
  const onWheelScrollArea = () => {
    pause();
    resumeSoon();
  };
  const onMouseEnterArea = () => {
    pause();
  };
  const onMouseLeaveArea = () => {
    resumeSoon();
  };

  /** ---------------------------
   * Track click-to-seek (edge-aware)
   * --------------------------- */
  const seekFromTrack = useCallback(
    (clientX) => {
      const el = scrollRef.current;
      const track = trackRef.current;
      const thumb = thumbRef.current;
      if (!el || !track || !thumb) return;

      const maxScroll = getMaxScroll();
      const trackRect = track.getBoundingClientRect();
      const trackW = track.clientWidth;
      const thumbW = thumb.clientWidth || MIN_THUMB_PX;

      if (maxScroll <= 0) return;

      // Target thumb center at click
      let left = clientX - trackRect.left - thumbW / 2;
      left = Math.max(0, Math.min(trackW - thumbW, left));

      // Clamp to ends if clicking near edges.
      if (left >= trackW - thumbW - EPS) {
        el.scrollTo({ left: maxScroll, behavior: 'smooth' });
        return;
      }
      if (left <= EPS) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }

      const ratio = trackW - thumbW > 0 ? left / (trackW - thumbW) : 0;
      const target = ratio * maxScroll;

      el.scrollTo({
        left: Math.max(0, Math.min(maxScroll, target)),
        behavior: 'smooth',
      });
    },
    [getMaxScroll]
  );

  const onTrackMouseDown = (e) => {
    if (e.target === thumbRef.current) return; // thumb has its own handler
    pause();
    seekFromTrack(e.clientX);
    resumeSoon();
  };

  /** ---------------------------
   * Thumb dragging (clamp & safe release)
   * --------------------------- */
  const onThumbDown = (e) => {
    const el = scrollRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!el || !track || !thumb) return;

    draggingThumbRef.current = true;
    pause();

    dragStartXRef.current = e.clientX;
    initialScrollLeftRef.current = el.scrollLeft;

    document.addEventListener('mousemove', onThumbMove);
    document.addEventListener('mouseup', onThumbUp);
    e.preventDefault();
  };

  const onThumbMove = (e) => {
    if (!draggingThumbRef.current) return;

    const el = scrollRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!el || !track || !thumb) return;

    const maxScroll = getMaxScroll();
    if (maxScroll <= 0) return;

    const deltaX = e.clientX - dragStartXRef.current;
    const trackUsable = Math.max(1, track.clientWidth - thumb.clientWidth);
    const scrollDelta = (deltaX / trackUsable) * maxScroll;

    let next = initialScrollLeftRef.current + scrollDelta;
    next = Math.max(0, Math.min(maxScroll, next));

    el.scrollLeft = next;
    scheduleThumbUpdate();
  };

  const snapToNearestCard = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    // If we're at either edge, DO NOT snap (prevents edge bounce-back).
    if (isAtLeftEdge() || isAtRightEdge()) return;

    const children = Array.from(el.children || []);
    if (!children.length) return;

    const curr = el.scrollLeft;
    let bestLeft = null;
    let bestDelta = Infinity;

    for (let i = 0; i < children.length; i++) {
      const left = children[i].offsetLeft;
      const d = Math.abs(left - curr);
      if (d < bestDelta) {
        bestDelta = d;
        bestLeft = left;
      }
    }

    // Only snap if we're meaningfully off (avoid tiny nudge).
    if (bestLeft !== null && Math.abs(bestLeft - curr) > SNAP_PX) {
      const max = getMaxScroll();
      el.scrollTo({
        left: Math.max(0, Math.min(max, bestLeft)),
        behavior: 'smooth',
      });
    }
  }, [getMaxScroll, isAtLeftEdge, isAtRightEdge]);

  const onThumbUp = () => {
    if (!draggingThumbRef.current) return;
    draggingThumbRef.current = false;
    document.removeEventListener('mousemove', onThumbMove);
    document.removeEventListener('mouseup', onThumbUp);

    // If thumb released near an edge, clamp hard to that edge and don't snap back.
    const el = scrollRef.current;
    if (el) {
      const max = getMaxScroll();
      if (el.scrollLeft >= max - EPS) {
        el.scrollLeft = max; // stick the landing
        scheduleThumbUpdate();
      } else if (el.scrollLeft <= EPS) {
        el.scrollLeft = 0;
        scheduleThumbUpdate();
      } else {
        snapToNearestCard();
      }
    }

    resumeSoon();
  };

  /** ---------------------------
   * Navigation
   * --------------------------- */
  const handleSelectQuote = (quote) => {
    if (!quote?.navigateTo) return;
    navigate(quote.navigateTo, {
      state: {
        fromCrossSell: true,
        crossSellStorageKey: quote.crossSellStorageKey,
      },
    });
  };

  if (quotes.length === 0) return null;

  const hasBestDeal = quotes.some((q) => q.bestDeal === true);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        pb: '20px',
      }}
    >
      <Box
        sx={{
          maxWidth: '1200px',
          mx: 'auto',
          px: 5,
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '1.25rem',
            lineHeight: '1.75rem',
            letterSpacing: '-0.03em',
            pb: 2,
          }}
        >
          {variant === 'crossSell'
            ? 'Compare more deals'
            : 'Your most recent quotes'}
        </Typography>
      </Box>
      {/* Ping-pong Marquee Track (single set) */}
      <Box
        ref={scrollRef}
        onPointerDown={onPointerDownScrollArea}
        onPointerUp={onPointerUpScrollArea}
        onTouchStart={onTouchStartArea}
        onTouchEnd={onTouchEndArea}
        onWheel={onWheelScrollArea}
        onMouseEnter={onMouseEnterArea}
        onMouseLeave={onMouseLeaveArea}
        sx={{
          display: 'flex',
          justifyContent: hasOverflow ? 'flex-start' : 'center',
          overflowX: 'scroll',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          maxWidth: '100%',
          gap: '24px',
          padding: '16px',
          mb: '8px',
          scrollSnapType: 'none',
          WebkitOverflowScrolling: 'touch',
          willChange: 'scroll-position',
          transform: 'translateZ(0)',
        }}
      >
        {quotes.map((quote, i) => (
          <Box key={`${quote.QuotePK}-${i}`} sx={{ flex: '0 0 auto' }}>
            {variant === 'crossSell' ? (
              <ProviderTileComponentCrossSellCarousel
                data={quote}
                isMobile={isMobile}
                onClick={() => handleSelectQuote(quote)}
                reserveBannerSpace={hasBestDeal}
              />
            ) : (
              <ProviderTileComponentCarousel
                data={quote}
                isMobile={isMobile}
                onClick={() => handleSelectQuote(quote)}
                reserveBannerSpace={hasBestDeal}
              />
            )}
          </Box>
        ))}
      </Box>

      {/* Custom Scrollbar */}
      <Box
        ref={trackRef}
        onMouseDown={onTrackMouseDown}
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: '8px',
          transform: 'translateX(-50%)',
          width: isMobile ? '100%' : '596px',
          height: '8px',
          borderRadius: '24px',
          backgroundColor: '#E0E0E0',
          overflow: 'hidden',
          cursor: 'pointer',
          userSelect: 'none',
          visibility: hasOverflow ? 'visible' : 'hidden',
        }}
      >
        <Box
          ref={thumbRef}
          onMouseDown={onThumbDown}
          sx={{
            position: 'relative',
            height: '8px',
            borderRadius: '1000px',
            backgroundColor: '#C7CA00',
            transition: draggingThumbRef.current ? 'none' : 'left 0.08s linear',
            cursor: 'grab',
          }}
        />
      </Box>
    </Box>
  );
};

export default QuoteCarouselComponent;
